import { getHealthcareById, getServicesCarouselDetail } from '@api/clinic/clinic';
import request from '@api/request';
import { MEDICAL_SERVICES_GROUPS_CAROUSEL_REC_ID } from '@constant/medical-services';
import {
  GetServicesCarouselDataModel,
  GetServicesCarouselDetailResponseModel,
  HealthcareIdEnum,
} from '@models/clinics';
import { BannerItem } from '@api/promotion/promotion.helpers';
import {
  MedicalServiceGroupPageProps,
  MedicalServicesPageProps,
  SearchServiceResponse,
  ServicesCarouselModel,
} from '@models/medical-services';

import {
  findHealthcareGroupByRecId,
  findHealthcareGroupBySlug,
  mapClinicCarouselsToMedicalCarousels,
  mapHealthcareItemsToLevel2,
  mapHealthcareProductsToLevel2,
} from './healthcare.helpers';
import {
  getActiveCarousels,
  resolveMedicalServiceGroupId,
  ServicesCarouselsApiResponse,
  unwrapCarousels,
} from './medical-services.helpers';

type BannersApiResponse = {
  queryResult?: BannerItem[];
  data?: BannerItem[];
};

export const getServicesCarousels = async (): Promise<
  ServicesCarouselModel[]
> => {
  const response = await request.get<
    ServicesCarouselsApiResponse | ServicesCarouselModel[]
  >('/ServicesCarousel/get', { withOutToken: true, err: false });

  const carousels = unwrapCarousels(
    response,
  ) as unknown as GetServicesCarouselDataModel[];

  return getActiveCarousels(mapClinicCarouselsToMedicalCarousels(carousels));
};

export const MEDICAL_SERVICES_CAROUSEL_DETAILS_PAGE_SIZE = 10;

export const getMedicalServicesCarouselDetails = async (
  carouselId: number,
  pageNumber = 1,
  pageSize = MEDICAL_SERVICES_CAROUSEL_DETAILS_PAGE_SIZE,
  search?: string,
): Promise<GetServicesCarouselDetailResponseModel> =>
  getServicesCarouselDetail({
    CarouselId: carouselId,
    pageNumber: String(pageNumber),
    pageSize: String(pageSize),
    ...(search ? { Search: search } : {}),
  });

export const getMedicalServicesBanners = async (): Promise<BannerItem[]> => {
  const response = await request.get<BannersApiResponse | BannerItem[]>(
    '/Promotion/GetBanners',
    { withOutToken: true, err: false },
  );

  const banners = Array.isArray(response)
    ? response
    : (response?.queryResult ?? response?.data ?? []);

  return banners
    .filter((banner) => banner?.isActive && banner?.icon)
    .map((banner) => ({
      ...banner,
      title: banner.title ?? '',
    }));
};

export const searchService = async (
  query: string,
): Promise<SearchServiceResponse> => {
  const response = await request.get('/Service/management/SearchService', null, {
    params: {
      Query: query,
    },
  });

  if (response?.queryResult) {
    return response;
  }

  if (response?.data?.queryResult) {
    return response.data;
  }

  return {
    queryResult: [],
    pageNumber: 1,
    pageSize: 0,
    totalCount: 0,
  };
};

export const getMedicalServicesPageData =
  async (): Promise<MedicalServicesPageProps> => {
    const [banners, servicesCarousels] = await Promise.all([
      getMedicalServicesBanners(),
      getServicesCarousels(),
    ]);

    return { banners, servicesCarousels };
  };

export const getMedicalServiceGroupPageData = async (
  groupIdOrSlug: string,
  recId?: string,
): Promise<MedicalServiceGroupPageProps> => {
  const [servicesTree, servicesCarousels] = await Promise.all([
    getHealthcareById({
      id: HealthcareIdEnum.services,
      pageSize: '50',
      pageNumber: '1',
    }),
    getServicesCarousels(),
  ]);

  const queryResult = servicesTree?.queryResult ?? [];

  const group =
    findHealthcareGroupByRecId(recId ?? '', queryResult) ??
    findHealthcareGroupBySlug(groupIdOrSlug, queryResult);

  const carouselGroupId =
    resolveMedicalServiceGroupId(
      groupIdOrSlug,
      servicesCarousels,
      MEDICAL_SERVICES_GROUPS_CAROUSEL_REC_ID,
    ) ??
    recId ??
    groupIdOrSlug;

  const level2Items = mapHealthcareProductsToLevel2(group?.products ?? []);
  const groupChildren = mapHealthcareItemsToLevel2(group?.children ?? []);

  const pageTitle = group?.name ? `خدمات ${group.name}` : 'خدمات درمانی';
  const searchPlaceholder = group?.name
    ? `جستجوی خدمت ${group.name}`
    : 'جستجوی خدمت';

  return {
    groupId: groupIdOrSlug,
    resolvedGroupId: carouselGroupId,
    pageTitle,
    searchPlaceholder,
    level2Items,
    groupChildren,
    servicesCarousels,
  };
};
